import { useCallback } from 'react';
import { ShortcutKeyView } from '@mark43/cad-api';
import { debounce } from 'lodash';
import { v4 } from 'uuid';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import {
    EventPanelData,
    useEntityPanelManager,
} from '../panels/entity-panels/monitored-entities/state/entity-panel-manager';
import { useWorkspaceStore } from '../panels/use-workspace-store';
import { useIntegratedActionBroadcaster } from '../action-broadcaster/use-integrated-action-broadcaster';
import { useEventNotificationsStore } from '../event-notifications/use-event-notifications';

export function useHandleEventsWindowShortcutKeys(): (shortcutKeyView: ShortcutKeyView) => void {
    const workspacePanelsStore = useWorkspaceStore();
    const eventPanelManager = useEntityPanelManager();
    const { trigger } = useIntegratedActionBroadcaster();
    const drawerControls = useDrawerControls();
    const eventNotificationsStore = useEventNotificationsStore();

    const debouncedSaveEvent = debounce(
        (eventPanel: EventPanelData) => {
            eventPanel.saveEvent();
        },
        500,
        { leading: true, trailing: false }
    );

    return useCallback(
        (shortcutKeyView: ShortcutKeyView) => {
            const focusedPanel = workspacePanelsStore.getFocusedPanel();
            if (!focusedPanel || focusedPanel.linkedEntityType !== 'CAD_AGENCY_EVENT') {
                return;
            }
            const eventPanel = eventPanelManager.getEventPanel(focusedPanel.uuid);
            if (!eventPanel) {
                return;
            }

            focusedPanel.notifyActivity();

            const isNewEvent = focusedPanel.linkedEntityId === undefined;

            switch (shortcutKeyView.shortcutCommand) {
                case 'CLEAR_ALL_UPDATES_FOR_EVENT': {
                    if (focusedPanel.linkedEntityId) {
                        eventNotificationsStore.removeAllNotificationsForEvent(
                            focusedPanel.linkedEntityId
                        );
                    }
                    break;
                }
                case 'EVENT_ASSIGN': {
                    if (focusedPanel.linkedEntityId) {
                        drawerControls.open({
                            type: 'manage-units',
                            props: {
                                eventId: focusedPanel.linkedEntityId,
                            },
                        });
                    }
                    break;
                }
                case 'EVENT_CAUTIONS_TAB': {
                    if (isNewEvent) {
                        break;
                    }
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_V2',
                            type: 'SET_EVENT_PANEL_TAB',
                            payload: {
                                workspacePanelClientUuid: focusedPanel.uuid,
                                tab: 'CAUTIONS',
                            },
                        },
                    ]);
                    break;
                }
                case 'EVENT_CLEAR': {
                    eventPanel.clearEvent();
                    break;
                }
                case 'EVENT_SAVE': {
                    debouncedSaveEvent(eventPanel);
                    break;
                }
                case 'EVENT_EMERGENCY': {
                    eventPanel.transmitEvent();
                    break;
                }
                case 'EVENT_ANI_TO_REPORTING_PERSON': {
                    eventPanel.callImport.importReportingPartyName();
                    eventPanel.callImport.importReportingPartyPhoneNumber();
                    break;
                }
                case 'EVENT_ALI_TO_LOCATION': {
                    eventPanel.callImport.importGpsOrAddress();
                    break;
                }
                case 'EVENT_IMPORT_ANI_ALI_TO_EVENT': {
                    eventPanel.callImport.importAll();
                    break;
                }
                case 'EVENT_RELATED_DATA': {
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_V2',
                            type: 'SET_EVENT_PANEL_TAB',
                            payload: {
                                workspacePanelClientUuid: focusedPanel.uuid,
                                tab: 'RELATED_DATA',
                            },
                        },
                    ]);
                    break;
                }
                case 'EVENT_LINKED_EVENTS': {
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_V2',
                            type: 'SET_EVENT_PANEL_TAB',
                            payload: {
                                workspacePanelClientUuid: focusedPanel.uuid,
                                tab: 'MERGED_EVENTS',
                            },
                        },
                    ]);
                    break;
                }
                case 'EVENT_EVENT_LOG': {
                    if (isNewEvent) {
                        break;
                    }
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_V2',
                            type: 'SET_EVENT_PANEL_TAB',
                            payload: {
                                workspacePanelClientUuid: focusedPanel.uuid,
                                tab: 'EVENT_LOG',
                            },
                        },
                    ]);
                    break;
                }
                case 'EVENT_CALL': {
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_V2',
                            type: 'SET_EVENT_PANEL_TAB',
                            payload: {
                                workspacePanelClientUuid: focusedPanel.uuid,
                                tab: 'ANI_ALI_RAPIDSOS',
                            },
                        },
                    ]);
                    break;
                }
                case 'EVENT_PDF': {
                    eventPanel.generatePDF();
                    break;
                }
                case 'EVENT_GENERATE_REN': {
                    eventPanel.generateRen();
                    break;
                }
            }
        },
        [
            workspacePanelsStore,
            eventPanelManager,
            eventNotificationsStore,
            drawerControls,
            trigger,
            debouncedSaveEvent,
        ]
    );
}
